import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Dataset, ListResponse, WorkflowType, WorkflowV1Read } from '@ca/core/models';
import { MachineLearningService, UserNotificationService, WorkflowV1Service } from '@ca/core/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store/src/models';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import * as actions from './machine-learning.actions';

@Injectable()
export class MachineLearningEffects {
    loadTrainingWorkflows$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadTrainingWorkflows),
            switchMap(() =>
                this.workflowV1Service
                    .getPublicList(
                        WorkflowType.SEGMENTATION_TRAINING,
                        new HttpParams().set('page[number]', 1).set('page[size]', 10000).set('type', WorkflowType.SEGMENTATION_TRAINING),
                        false,
                    )
                    .pipe(
                        map((resp: ListResponse<WorkflowV1Read>) => actions.loadTrainingWorkflowsSuccess({ trainingWorkflows: resp.data || [] })),
                        catchError(() => {
                            return of(
                                actions.loadTrainingWorkflowsFailed({
                                    error: this.translateService.instant('MACHINE_LEARNING.TRAIN.ERRORS.GET_TRAININGS'),
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    loadSegmentationWorkflows$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadSegmentationWorkflows),
            switchMap(() =>
                this.workflowV1Service
                    .getPublicList(
                        WorkflowType.SEGMENTATION,
                        new HttpParams().set('page[number]', 1).set('page[size]', 10000).set('type', WorkflowType.SEGMENTATION),
                        false,
                    )
                    .pipe(
                        map((resp: ListResponse<WorkflowV1Read>) => actions.loadSegmentationWorkflowsSuccess({ segmentationWorkflows: resp.data || [] })),
                        catchError(() => {
                            return of(
                                actions.loadSegmentationWorkflowsFailed({
                                    error: this.translateService.instant('MACHINE_LEARNING.SEGMENT.LOAD_SEGMENTATION_WORKFLOWS_FAILED'),
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    loadDatasets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadDatasets),
            tap(() => this.userNotificationService.showSpinner()),
            switchMap(props =>
                this.machineLearningService.fetchDatasets(props.payload ? props.payload.searchParams : undefined).pipe(
                    map((resp: ListResponse<Dataset>) => {
                        return {
                            datasets: resp.data || [],
                            totalCount: resp.meta && resp.meta.totalCount != null ? resp.meta.totalCount : 0,
                        };
                    }),
                    switchMap((returnValue: { datasets: Dataset[]; totalCount: number }) => {
                        // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
                        const ngrxActions: ({ datasets: Dataset[]; totalCount: number } & Action<string>) | Action<string>[] = [];
                        ngrxActions.push(
                            actions.loadDatasetsSuccess({
                                datasets: returnValue.datasets,
                                totalCount: returnValue.totalCount,
                            }),
                        );
                        if (props.payload && props.payload.searchParams?.searchQuery?.length > 0) {
                            // if there is a search query the totalCount of datasets resembles the total number of items fitting the search query
                            ngrxActions.push(actions.loadNumberOfAllDatasets());
                        } else {
                            ngrxActions.push(actions.loadNumberOfAllDatasetsSuccess({ numberOfAllDatasets: returnValue.totalCount }));
                        }

                        return ngrxActions;
                    }),
                    catchError(() => {
                        const translatedStr = this.translateService.instant('MACHINE_LEARNING.ANNOTATE.LOAD_DATASETS_FAILED');
                        this.userNotificationService.showError(translatedStr);
                        return of(actions.loadDatasetsFailed({ errorMsg: translatedStr }));
                    }),
                    finalize(() => this.userNotificationService.hideSpinner()),
                ),
            ),
        ),
    );

    loadNumberOfAllDatasets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadNumberOfAllDatasets),
            switchMap(() =>
                this.machineLearningService.fetchDatasets().pipe(
                    map((resp: ListResponse<Dataset>) => {
                        return actions.loadNumberOfAllDatasetsSuccess({
                            numberOfAllDatasets: resp.meta && resp.meta.totalCount != null ? resp.meta.totalCount : 0,
                        });
                    }),
                    catchError(() => {
                        this.userNotificationService.hideSpinner();
                        const translatedStr = this.translateService.instant('MACHINE_LEARNING.ANNOTATE.LOAD_DATASETS_FAILED');
                        this.userNotificationService.showError(translatedStr);
                        return of(actions.loadNumberOfAllDatasetsFailed({ errorMsg: translatedStr }));
                    }),
                ),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private machineLearningService: MachineLearningService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private workflowV1Service: WorkflowV1Service,
    ) {}
}
